import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Wrench, CircuitBoard, Car, Battery, Lightbulb, Waves } from 'lucide-react';


const services = [
  { icon: Wrench, title: 'Electrical Troubleshooting & Repairs', desc: 'Fast diagnosis and expert resolution of electrical issues. From flickering lights to circuit overloads, we pinpoint and fix problems safely.', span: 'md:col-span-4', link: '/services/electrical-repair-nashville' },
  { icon: CircuitBoard, title: 'Circuit Breaker Panel Replacement', desc: 'Upgrade your outdated panel to handle modern power demands. Safe, code-compliant installations from 100A to 400A service.', span: 'md:col-span-4', link: '/services/electrical-panel-upgrade-nashville' },
  { icon: Car, title: 'EV Charging Installation', desc: 'Professional home EV charger installation with electrical-capacity assessment and dedicated-circuit planning where applicable.', span: 'md:col-span-4', link: '/services/ev-charger-installation-nashville' },
  { icon: Battery, title: 'Generator & Backup Power', desc: 'Whole-home standby generators and battery backup systems through our Generators of Nashville division. Never lose power again.', span: 'md:col-span-6', link: 'https://generatorsofnashville.com' },
  { icon: Lightbulb, title: 'Lighting, Fan & Switch Installation', desc: 'Recessed lighting, ceiling fans, smart switches, and outlet installations. Transform your space with expert electrical upgrades.', span: 'md:col-span-3', link: '/services/lighting-installation-nashville' },
  { icon: Waves, title: 'Pool, Hot Tub & Spa Wiring', desc: 'Specialized wiring for pools, hot tubs, and spas. GFCI protection, bonding, and code-compliant outdoor electrical work.', span: 'md:col-span-3', link: '/services/pool-spa-wiring-nashville' },
];

// Extracted animation constants - stable references prevent re-renders
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.08 } }),
};
const fadeIn = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const halfSecTransition = { duration: 0.5 };
const delayedTransition = { duration: 0.5, delay: 0.1 };
const viewportOnce = { once: true };

function ServiceCard({ svc, index }) {
  return (
    <motion.div
      key={svc.title}
      initial="hidden" whileInView="visible" viewport={viewportOnce}
      variants={fadeUp} custom={index}
      className={`service-card border border-black bg-white p-8 block ${svc.span}`}
      data-testid={`service-card-${index}`}
    >
      <svc.icon className="service-icon w-8 h-8 text-yellow-500 mb-4" />
      <h3 className="service-title font-heading text-lg font-bold uppercase tracking-tight mb-3">{svc.title}</h3>
      <p className="service-desc font-body text-sm text-zinc-600 leading-relaxed">{svc.desc}</p>
      {svc.link?.startsWith('http') ? (
        <a href={svc.link} className="inline-block mt-4 font-body text-xs uppercase tracking-widest text-yellow-600 hover:text-black transition-colors" data-testid="service-card-backup-power-link">
          View Details &rarr;
        </a>
      ) : svc.link ? (
        <Link to={svc.link} className="inline-block mt-4 font-body text-xs uppercase tracking-widest text-yellow-600 hover:text-black transition-colors">
          View Details &rarr;
        </Link>
      ) : (
        <a href="#contact" className="inline-block mt-4 font-body text-xs uppercase tracking-widest text-zinc-400 group-hover:text-black">
          Learn More &rarr;
        </a>
      )}
    </motion.div>
  );
}

export default function ServicesSection() {
  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32 px-6 bg-zinc-50">
      <div className="max-w-7xl mx-auto">
        <motion.p {...fadeIn} className="font-body text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Nashville&apos;s Trusted Electrical Services</motion.p>
        <motion.h2 {...fadeInUp} transition={halfSecTransition} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase mb-4">
          Professional Electrical Services in Nashville, TN
        </motion.h2>
        <motion.p {...fadeInUp} transition={delayedTransition} className="font-body text-zinc-600 max-w-2xl mb-12">
          Trusted by Nashville homeowners and businesses. From emergency repairs to complete installations.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {services.map((svc, i) => <ServiceCard key={svc.title} svc={svc} index={i} />)}
        </div>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/services/residential-electrical-services-nashville" className="inline-flex items-center gap-2 bg-black text-white font-heading font-bold text-xs uppercase tracking-wider px-6 py-3 border border-black hover:-translate-y-0.5 transition-transform" data-testid="services-link-residential">
            Residential Services &rarr;
          </Link>
          <Link to="/services/commercial-electrical-services-nashville" className="inline-flex items-center gap-2 bg-white text-black font-heading font-bold text-xs uppercase tracking-wider px-6 py-3 border border-black hover:-translate-y-0.5 transition-transform" data-testid="services-link-commercial">
            Commercial Services &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
